import React, { FC } from 'react';
import styled from '@emotion/styled';
import Heading from '~ComponentsImproved/Typography/Heading';
import TopThree from '../TopThree';
import PrintScore from '../PrintScore';
import ViewLeaderboard from '../ViewLeaderboard';
import TimeLeftIndicator from '../TimeLeftIndicator';
import { GetCompetitions_Venue_competitions } from '~Providers/CompetitionsProvider/__queries__';
import { hasSpecificSales } from '../../utils';
import { getCompetitionDateStatus } from '~Providers/CompetitionsProvider/utils';
import { Box } from '~Styles';
import {
  GetStaffAndCategories_user_company_categories,
  GetItems_items_results,
} from '../../__queries__';
import { handyBreakpoint } from '~Styles/breakpoints';

const CompetitionBox = styled(Box)`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  background-color: var(--colorBlueGray1100);
  border-radius: 8px;
  padding: 24px;

  ${handyBreakpoint} {
    padding: 16px;
    margin-bottom: 16px;
    border: none;
  }
`;

const InfoWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  border-bottom: 1px solid var(--colorBlueGray1000);
  padding-bottom: 16px;

  ${handyBreakpoint} {
    padding-bottom: 8px;
    gap: 4px;
  }
`;

const LeaderboardWrapper = styled.div`
  display: flex;
  flex-grow: 1;
  justify-content: space-between;
  align-items: center;
  flex-direction: column;
  gap: 16px;
  padding-top: 16px;

  ${handyBreakpoint} {
    gap: 8px;
    padding-top: 8px;
  }
`;

interface Props {
  competition: GetCompetitions_Venue_competitions;
  isHandy?: boolean;
  categories: GetStaffAndCategories_user_company_categories[];
  items: GetItems_items_results[];
}

const CompetitionItem: FC<Props> = ({ competition, isHandy, categories, items }) => {
  const hasOnlySpecificSales = hasSpecificSales(competition.onlySpecificSales);
  const compStatus = getCompetitionDateStatus(competition.startDate, competition.endDate);

  return (
    <CompetitionBox key={competition.id}>
      <InfoWrapper>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" flexDirection="column" gap="xs">
            <Heading level={isHandy ? 5 : 3} color="var(--colorWhite)" bottom={0}>
              {competition.name}
            </Heading>
            <TimeLeftIndicator
              compStatus={compStatus}
              endDate={compStatus.isUpcoming ? competition.startDate : competition.endDate}
            />
          </Box>
          <PrintScore isHandy={isHandy} competitionId={competition.id} />
        </Box>
      </InfoWrapper>
      <LeaderboardWrapper>
        <TopThree
          competitionType={competition.type}
          hasOnlySpecificSales={hasOnlySpecificSales}
          leaderboard={competition.score}
        />
        <ViewLeaderboard competition={competition} categories={categories} items={items} />
      </LeaderboardWrapper>
    </CompetitionBox>
  );
};

export default CompetitionItem;
